// „Взаимозаменяеми продукти" — ОПЕРАЦИЯТА: PageHeader („N правила" + „Ново
// правило"), търсачка и ЕДНА карта с по един ред за всяко правило. Редът е
// същото равенство като в редактора („1 кг Кафе A = 0.7 кг Кафе B"), под него —
// кой се изписва пръв и коментарът. БЕЗ KPI лента — правилото се чете от
// самите чипове. Без правила — празно състояние с пример, което обяснява какво
// е правило.
//
// Групите са ОБЩИ за всички складове (`GET /inventory/interchange-groups` без
// warehouseId). Създаването и редакцията са отделни страници (редът отваря
// редакцията); изтриването живее в тяхната долна лента.
// Клавиатура: ↓ от търсачката → първото правило, ↑/↓ по редовете (↑ от
// първия — обратно в търсачката), Enter отваря.
import { useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInterchangeGroups } from "../../../hooks/useInventory";
import { InventoryTheme } from "../components/InventoryTheme";
import { KbdChip } from "../components/KbdChip";
import { PageHeader } from "../components/list/PageHeader";
import { SearchBox, SectionCard } from "../components/list/ListBits";
import { LINK_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { InterchangeRuleExample } from "./components/InterchangeChainChips";
import { InterchangeGroupCard } from "./components/InterchangeGroupCard";

// От общите props на складовите страници (Inventory.tsx) трябва само
// тесният изглед; бутонът за менюто е в PageHeader (useInventoryShell).
type Props = { isNarrow?: boolean };

export const InterchangeablesPage = ({ isNarrow = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  const { data: groups = [], isLoading } = useInterchangeGroups();

  // Търсене дума по дума по продуктите и коментара на групата.
  const searchWords = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );
  const visibleGroups = useMemo(() => {
    if (searchWords.length === 0) return groups;
    return groups.filter((group) => {
      const haystack = [...group.members.map((member) => member.productName), group.hint ?? ""]
        .join(" ")
        .toLowerCase();
      return searchWords.every((word) => haystack.includes(word));
    });
  }, [groups, searchWords]);

  const startNew = () => navigate("/inventory/interchangeables/new");
  const newButton = (
    <button type="button" onClick={startNew} className={PRIMARY_BUTTON_CLASS}>
      <PlusOutlined className="text-[13px]" />
      {t("inventoryPage.newInterchange")}
    </button>
  );

  const rowsOf = () =>
    Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-interchange-row]") ?? []);

  // ↓ от търсачката — към първото правило
  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" || !(event.target instanceof HTMLInputElement)) return;
    const first = rowsOf()[0];
    if (!first) return;
    event.preventDefault();
    first.focus();
  };

  // ↑/↓ между редовете-правила (фокусът е на самия ред); ↑ от първия — в търсачката
  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = rowsOf();
    const index = rows.indexOf(document.activeElement as HTMLElement);
    if (index < 0) return;
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    if (next < 0) {
      event.preventDefault();
      searchWrapRef.current?.querySelector("input")?.focus();
      return;
    }
    if (next >= rows.length) return;
    event.preventDefault();
    rows[next].focus();
  };

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.interchange")}
          subtitle={
            groups.length
              ? t("inventoryPage.interchangeRulesCount", { count: groups.length })
              : undefined
          }
          right={groups.length ? newButton : undefined}
        />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : groups.length === 0 ? (
          // Празно: какво е правило + пример в същия вид, в който ще се вижда
          <div className="flex flex-1 items-center justify-center overflow-y-auto p-7">
            <div className="flex w-full max-w-[520px] flex-col items-center gap-3 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-tint text-xl text-brand-deep-ink">
                <SwapOutlined />
              </span>
              <span className="text-[17px] font-semibold leading-[1.2] text-ink">
                {t("inventoryPage.ichEmptyTitle")}
              </span>
              <span className="text-sm leading-[1.5] text-body">
                {t("inventoryPage.ichEmptyText")}
              </span>
              <InterchangeRuleExample className="w-full bg-panel" />
              <div className="mt-1.5">{newButton}</div>
            </div>
          </div>
        ) : (
          <div
            className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
              isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
            }`}
          >
            <div ref={searchWrapRef} onKeyDown={onSearchKeyDown} className="flex flex-wrap items-center gap-2">
              <SearchBox
                value={search}
                onChange={setSearch}
                placeholder={t("inventoryPage.interchangeSearchPh")}
              />
            </div>
            <SectionCard>
              <div ref={listRef} onKeyDown={onListKeyDown}>
                {visibleGroups.map((group) => (
                  <InterchangeGroupCard
                    key={group.id}
                    group={group}
                    searchWords={searchWords}
                    onEdit={() => navigate(`/inventory/interchangeables/${group.id}/edit`)}
                  />
                ))}
                {visibleGroups.length === 0 && (
                  <div className="flex flex-col items-center gap-2 px-[18px] py-8 text-center">
                    <span className="text-sm leading-[1.4] text-body">
                      {t("inventoryPage.ichNoMatch", { query: search.trim() })}
                    </span>
                    <button type="button" onClick={() => setSearch("")} className={LINK_BUTTON_CLASS}>
                      {t("inventoryPage.ichClearSearch")}
                    </button>
                  </div>
                )}
              </div>
            </SectionCard>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 text-[12.5px] leading-relaxed text-muted">
              <span>{t("inventoryPage.interchangeHint")}</span>
              {!isNarrow && (
                <span className="inline-flex items-center gap-1.5">
                  <KbdChip>↑</KbdChip>
                  <KbdChip>↓</KbdChip>
                  <KbdChip>Enter</KbdChip>
                  {t("inventoryPage.ichListKeys")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </InventoryTheme>
  );
};

export default InterchangeablesPage;
